import { useEffect, useState } from "react";
import axios from "axios";
import { confirmSwal } from "../utils/confirm";
import { toast } from "react-toastify";
import { auth } from "../firebase";

export default function AdminResourcesEditor() {
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openIndex, setOpenIndex] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState({});
    const [filterTag, setFilterTag] = useState("all");

    useEffect(() => {
        const fetchResources = async () => {
            try {
                const token = await auth.currentUser.getIdToken();
                const res = await axios.get("/api/public/resources", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setResources(res.data.content || []);
            } catch (error) {
                console.error("Error fetching resources:", error);
                toast.error("Error fetching items");
            } finally {
                setLoading(false);
            }
        };
        
        fetchResources();
    }, []);


    const handleChange = (index, field, value) => {
        const updated = [...resources];
        updated[index][field] = value;
        setResources(updated);
    };

    const moveResources = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= resources.length) return;

        const updated = [...resources];
        const temp = updated[index];
        updated[index] = updated[newIndex];
        updated[newIndex] = temp;

        setResources(updated);
        setOpenIndex(newIndex);
    };

    const saveResources = async () => {
        setSaving(true);
        try {
            const token = await auth.currentUser.getIdToken();
            await axios.put("/api/public/resources", { content: resources }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.success("Resources saved!");
        } catch {
            toast.error("Failed to save resources");
        } finally {
            setSaving(false);
        }
    };

    const addResources = () => {
        const newResources = {
            label: "",
            image: "",
            tag: ""
        };
        setResources([newResources, ...resources]);
        setOpenIndex(0);
    };

    const deleteResources = async (index) => {
        const resourcesMember = resources[index];
        const ok = await confirmSwal({
            title: `Delete resources ${resourcesMember.label || "(No Name)"}?`,
            text: "Once deleted it cannot be restored.",
            icon: "warning",
            confirmText: "Delete",
            confirmColor: "#d33",
        });
        if (!ok) return;

        const updated = resources.filter((_, i) => i !== index);
        setResources(updated);
        setOpenIndex(null);

        try {
            const token = await auth.currentUser.getIdToken();
            await axios.put("/api/public/resources", { content: updated }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.info("Resources deleted");
        } catch {
            toast.error("Failed to save after deletion");
        }
    };

    const toggleOpen = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const handleImageUpload = async (index, file) => {
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Only JPG, PNG, HEIC and PDF files are allowed');
            return;
        }

        // Validate file size (10MB)
        if (file.size > 15 * 1024 * 1024) {
            toast.error('File size must be less than 15MB');
            return;
        }

        setUploading({ ...uploading, [index]: true });

        try {
            const token = await auth.currentUser.getIdToken();
            const formData = new FormData();
            formData.append('file', file);
            formData.append('subfolder', 'resources');

            const response = await axios.post('/api/upload-image', formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.success) {
                handleChange(index, 'image', response.data.url);
                toast.success('Resource uploaded successfully!');
            }
        } catch (error) {
            console.error('Error uploading resource:', error);
            toast.error(error.response?.data?.detail || 'Failed to upload resource');
        } finally {
            setUploading({ ...uploading, [index]: false });
        }
    };

    if (loading) return <p>Loading resources...</p>;

    // Get all unique tags
    const allTags = [...new Set(resources.map(r => r.tag).filter(tag => tag))].sort();
    
    // Filter resources by tag
    const filteredResources = filterTag === "all" 
        ? resources 
        : resources.filter(r => r.tag === filterTag);

    return (
        <div>
            <p className="text-body-secondary">Resource items are displayed on the <em>Resources</em> page.</p>

            <div className="d-flex justify-content-between align-items-center mb-3">
                <button className="btn btn-admin" onClick={addResources}>+ Add Resource</button>
                
                {allTags.length > 0 && (
                    <div className="d-flex align-items-center gap-2">
                        <label className="mb-0">Filter by tag:</label>
                        <select 
                            className="form-select form-select-sm" 
                            style={{ width: 'auto' }}
                            value={filterTag}
                            onChange={(e) => setFilterTag(e.target.value)}
                        >
                            <option value="all">All ({resources.length})</option>
                            {allTags.map(tag => (
                                <option key={tag} value={tag}>
                                    {tag} ({resources.filter(r => r.tag === tag).length})
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            <div className="accordion" id="projectsAccordion">
                {filteredResources.map((resourcesMember, index) => {
                    // Get the actual index in the full resources array
                    const actualIndex = resources.indexOf(resourcesMember);
                    return (
                    <div className="accordion-item" key={actualIndex}>
                        <h2 className="accordion-header" id={`heading-${actualIndex}`}>
                            <button
                                className={`accordion-button ${openIndex === actualIndex ? "" : "collapsed"}`}
                                type="button"
                                onClick={() => toggleOpen(actualIndex)}
                            >
                                <strong>{resourcesMember.label || "(No Label)"}</strong>
                                {resourcesMember.tag && (
                                    <span className="badge bg-secondary ms-2" style={{ fontSize: '0.75rem' }}>
                                        {resourcesMember.tag}
                                    </span>
                                )}
                            </button>
                        </h2>
                        <div className={`accordion-collapse collapse ${openIndex === actualIndex ? "show" : ""}`}>
                            <div className="accordion-body">
                                <div className="mb-2">
                                    <label className="form-label">Label</label>
                                    <input className="form-control" value={resourcesMember.label} onChange={(e) => handleChange(actualIndex, "label", e.target.value)} />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">Tag <small className="text-muted">(optional, for filtering)</small></label>
                                    <input 
                                        className="form-control" 
                                        value={resourcesMember.tag || ""} 
                                        onChange={(e) => handleChange(actualIndex, "tag", e.target.value)}
                                        placeholder="e.g., Forms, Policies, Guides"
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">Image or PDF</label>
                                    <input 
                                        className="form-control mb-2" 
                                        value={resourcesMember.image} 
                                        onChange={(e) => handleChange(actualIndex, "image", e.target.value)}
                                        placeholder="Image or PDF URL"
                                    />
                                    <div className="input-group">
                                        <input 
                                            type="file" 
                                            className="form-control" 
                                            accept=".jpg,.jpeg,.png,.heic,.pdf"
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) handleImageUpload(actualIndex, file);
                                            }}
                                            disabled={uploading[actualIndex]}
                                        />
                                        <button 
                                            className="btn btn-outline-secondary" 
                                            type="button"
                                            disabled
                                        >
                                            {uploading[actualIndex] ? 'Uploading...' : 'Upload'}
                                        </button>
                                    </div>
                                    {resourcesMember.image && (
                                        <div className="mt-2">
                                            <img 
                                                src={resourcesMember.image} 
                                                alt="Preview" 
                                                style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover' }}
                                                className="img-thumbnail"
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <button
                                            className="btn btn-sm btn-outline-secondary me-2"
                                            onClick={() => moveResources(actualIndex, -1)}
                                            disabled={actualIndex === 0}
                                        >
                                            ↑ Move Up
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => moveResources(actualIndex, 1)}
                                            disabled={actualIndex === resources.length - 1}
                                        >
                                            ↓ Move Down
                                        </button>
                                    </div>
                                    <button className="btn btn-outline-danger btn-sm" onClick={() => deleteResources(actualIndex)} disabled={saving}>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    );
                })}
            </div>

            <button className="btn btn-admin mt-3" onClick={saveResources} disabled={saving}>
                {saving ? "Saving..." : "Save"}
            </button>
        </div>
    );
}
